import { styled } from '@linaria/react';
import {
  inkDarkest,
  inkLight,
  primaryBase,
  redBase,
  secondaryBase,
  secondaryLighter,
  skyBase,
  skyDark,
  skyLight,
} from '../../../styles/design-tokens';
import { bucketEvents } from './session-trace-strip.helpers';
import type { SessionTraceStripProps } from './session-trace-strip.type';
import { EVENT_TYPE_COLOR, MAX_TICKS } from './session-trace-strip.type';

export function SessionTraceStrip({ session }: SessionTraceStripProps) {
  const allEvents = session.turns.flatMap((t) => t.events ?? []);
  if (allEvents.length === 0) return null;

  const ticks = bucketEvents(allEvents, MAX_TICKS);

  return (
    <Wrapper>
      <Label>Session trace</Label>
      <Strip>
        {ticks.map((tick) => {
          const color = EVENT_TYPE_COLOR[tick.key] ?? skyBase;
          const tall = tick.key === 'RESPONSE' || tick.key === 'ASSISTANT_MESSAGE';
          const extraTall = tick.key === 'ERROR' || tick.key === 'TOOL_ERROR';
          const title = tick.count > 1 ? `${tick.key} (×${tick.count})` : tick.key;
          return <Tick key={tick.id} color={color} tall={tall} extraTall={extraTall} title={title} />;
        })}
      </Strip>
      <LegendRow>
        <LegendItem>
          <LegendSwatch color={inkDarkest} />
          user
        </LegendItem>
        <LegendItem>
          <LegendSwatch color={secondaryBase} />
          assistant
        </LegendItem>
        <LegendItem>
          <LegendSwatch color={secondaryLighter} />
          think
        </LegendItem>
        <LegendItem>
          <LegendSwatch color={skyDark} />
          tool
        </LegendItem>
        <LegendItem>
          <LegendSwatch color={primaryBase} />
          result
        </LegendItem>
        <LegendItem>
          <LegendSwatch color={redBase} />
          error
        </LegendItem>
      </LegendRow>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${skyLight};
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 0;
  overflow: hidden;
`;

const Label = styled.div`
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
`;

const Strip = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
  height: 22px;
  padding: 0 2px;
  min-width: 0;
  width: 100%;
`;

const Tick = styled.div<{ color: string; tall: boolean; extraTall: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  max-width: 8px;
  height: ${(props) => (props.extraTall ? '18px' : props.tall ? '16px' : '10px')};
  background: ${(props) => props.color};
  border-radius: 1.5px;
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  font: 500 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LegendSwatch = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(props) => props.color};
`;
